import * as signalR from '@microsoft/signalr';

type DeltaNotification = { entity: string; operation: string; id: string; orgId: string };

export class SignalRClient {
  private connection: signalR.HubConnection | null = null;
  private token: string;
  private onDelta: (notification: DeltaNotification) => void;
  private onReconnect?: () => void;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 5000;
  private stopped = false;

  constructor(
    token: string,
    onDelta: (notification: DeltaNotification) => void,
    onReconnect?: () => void,
  ) {
    this.token = token;
    this.onDelta = onDelta;
    this.onReconnect = onReconnect;
  }

  async connect(): Promise<void> {
    if (this.stopped) return;
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;
    if (this.connection?.state === signalR.HubConnectionState.Connecting ||
        this.connection?.state === signalR.HubConnectionState.Reconnecting) return;

    const syncUrl = import.meta.env.VITE_SYNC_URL || '';
    const hubUrl = `${syncUrl}/sync-hub`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.token,
      })
      .withAutomaticReconnect({
        // Never give up — an extended outage (API container rebuild, tunnel blip) is
        // the normal case. Stops at 120s so we don't DoS ourselves.
        nextRetryDelayInMilliseconds: (ctx) => {
          const schedule = [0, 2000, 5000, 15000, 30000, 60000, 120000];
          return schedule[Math.min(ctx.previousRetryCount, schedule.length - 1)];
        },
      })
      // Suppress noisy console.error on expected background tab drops
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.connection.on('SyncDelta', (notification: DeltaNotification) => {
      this.onDelta(notification);
    });

    this.connection.onreconnected(() => {
      this.reconnectDelay = 5000;
      this.onReconnect?.();
    });

    // onclose fires only after withAutomaticReconnect exhausts all attempts
    this.connection.onclose(() => {
      if (!this.stopped) {
        this.scheduleReconnect();
      }
    });

    try {
      await this.connection.start();
      this.reconnectDelay = 5000;
    } catch {
      // start() failed (e.g. server unreachable) — schedule retry
      if (!this.stopped) {
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(delay * 2, 60000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  async disconnect(): Promise<void> {
    this.stopped = true;
    this.clearReconnectTimer();
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  resume(): void {
    this.stopped = false;
    this.reconnectDelay = 5000;
    this.connect();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  updateToken(token: string): void {
    this.token = token;
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      this.connection.stop().then(() => {
        if (!this.stopped) this.connect();
      });
    }
  }

  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}
